import * as Msal from 'msal';
import { Configuration as MsalConfiguration } from 'msal/lib-commonjs/Configuration';
import { AuthenticationParameters as MsalAuthenticationParameters } from 'msal/lib-commonjs/AuthenticationParameters';
import { AuthResponse } from 'msal/lib-commonjs/AuthResponse';
import { AuthError } from 'msal/lib-commonjs/error/AuthError';
import '../Utils/Extensions';
import { EventHandlerProvider } from './EventHandlerService';
import { VeriLiteErrorEvent, IValidationResult, IResources } from '../Types';
import { parseBoolean, postJson, GetResources, UserRequestHeaders } from './Util';
import Endpoints from '../Utils/Endpoints';

interface IAppConfig {
    b2cScopes: string[];
    webApi: string;
}

export interface IAuthenticatedUser {
    name: string;
    Location: string;
    Organisation: string;
    TwoFactorEnabled: boolean;
    PasswordReset: boolean;
    id: string;
}
export interface IAuthenticationService {
    getUser: () => IAuthenticatedUser;
    getAccessToken: () => Promise<string>;
    refreshAccessToken: () => Promise<string>;
    logout: () => void;
    init: (locationSetCallback: (location:string) => void) => void;
    readErrorFromSession: () => string;
    passwordReset: () => void;
    reAuthenticateAfterPasswordReset: () => void;
    editProfile: () => void;
    setError: () => void;
    twoFactorComplete: (code:string) => void;
    sessionExpiry: number;
    isAuthenticated: boolean;
    isCallback: boolean;
    isPasswordReset: boolean;
    hasError: boolean;
    getError: () => string;
    State: AuthenticationState
}

export enum AuthenticationState {
    Unauthenticated,
    Loading,
    Authenticated,
    PasswordReset,
    TwoFactor,
    LicenseError,
    Error
}

export class AuthenticationService implements IAuthenticationService {
    private appConfig: IAppConfig;
    private msalConfig: MsalConfiguration;
    private loginRequest: MsalAuthenticationParameters;
    private tokenRequest: MsalAuthenticationParameters;
    private authClient: Msal.UserAgentApplication;
    private accessToken: string;
    private secondsIdle: number;
    private errorKey: string = "msal.login.error";
    private passwordResetKey: string = "password.reset";
    private editProfileKey: string = "edit.profile";
    private twoFactorKey: string = "2fa.code";
    public static readonly selectedLocationKey: string = "selectedLocation";
    public sessionExpiry: number;
    public isAuthenticated: boolean;
    public isCallback: boolean;
    public isPasswordReset: boolean;
    public isEditProfile: boolean;
    public hasError: boolean;
    private resources: IResources;
    private _state: AuthenticationState;
    public get State(): AuthenticationState {
        return this._state;
    }

    constructor() {
        this.resources = {};
        GetResources((resources: IResources) => { this.resources = resources; });

        this.appConfig = {
            b2cScopes: [process.env.REACT_APP_B2CSCOPES || ""],
            webApi: process.env.REACT_APP_WEBAPI || ""
        };

        this.msalConfig = {
            auth: {
                clientId: process.env.REACT_APP_CLIENTID || "",
                authority: process.env.REACT_APP_SIGNUPSIGNIN || "",
                validateAuthority: false,
                postLogoutRedirectUri: process.env.REACT_APP_WEBAPI || ""
            },
            cache: {
                cacheLocation: "sessionStorage",
                storeAuthStateInCookie: false
            }
        };

        this.loginRequest = {
            scopes: this.appConfig.b2cScopes,
            redirectUri: process.env.REACT_APP_WEBAPI || ""
        };

        this.tokenRequest = {
            scopes: this.appConfig.b2cScopes,
            redirectUri: process.env.REACT_APP_WEBAPI || "",
            forceRefresh: true
        };

        this.accessToken = "";
        this.secondsIdle = 0;
        this.sessionExpiry = 0;

        this.authClient = new Msal.UserAgentApplication(this.msalConfig);
        this.authClient.handleRedirectCallback(this.authCallback);
        this.isCallback = this.authClient.isCallback(window.location.hash);
        this.hasError = sessionStorage.getItem(this.errorKey) !== null;
        this.isAuthenticated = this.authClient.getAccount() !== null;
        this.isPasswordReset = this.checkSessionState(this.passwordResetKey);
        this.isEditProfile = this.checkSessionState(this.editProfileKey);
        if (this.isCallback) {
            this._state = AuthenticationState.Loading;
        } 
        else if (this.isPasswordReset) {
            this._state = AuthenticationState.PasswordReset;
        }
        else if (this.hasError) {
            this._state = AuthenticationState.Error;
            var error = sessionStorage.getItem(this.errorKey);
            console.error(error);
            if (this.isPasswordReset) {
                this._state = AuthenticationState.PasswordReset;
            }
            else if (error?.startsWith("AADB2C90118")) {
                sessionStorage.removeItem(this.errorKey);
                this.passwordReset();
            } 
        } else if (this.isAuthenticated) {
            this._state = AuthenticationState.Authenticated;
        } else {
            this._state = AuthenticationState.Unauthenticated;
        }
    }

    public async init(locationSetCallback: (location: string)=>void) {
        if (!this.isAuthenticated) {
            if (this.hasError) {
                console.error(sessionStorage.getItem(this.errorKey));
            } else {
                this.authClient.loginRedirect(this.loginRequest);
            }
        }
        else {            
            if (!this.isCallback) {
                if (this.isEditProfile) {
                    sessionStorage.setItem(this.editProfileKey, 'false');
                    sessionStorage.removeItem(this.errorKey);
                    this.authClient.loginRedirect(this.loginRequest);
                } else if (this.isPasswordReset) {
                    this._state = AuthenticationState.PasswordReset;
                } else if (this.getUser().PasswordReset) {
                    this.passwordReset()
                } else if (this.getUser().TwoFactorEnabled && !(await this.validateCodeAsync(this.getTwoFactorCode() || "")).isValid) {
                    this._state = AuthenticationState.TwoFactor
                } else {
                    //the idToken exp value is in seconds and we want miliseconds
                    this.sessionExpiry = Number(this.authClient.getAccount().idToken.exp) * 1000;
                    this.initLogoutTimer();
                    //console.log(`Setting session storage with users default location: ${this.getUser().Location}`);
                    if (sessionStorage.getItem(AuthenticationService.selectedLocationKey) == null) {
                        sessionStorage.setItem(AuthenticationService.selectedLocationKey, this.getUser().Location);
                        locationSetCallback(this.getUser().Location);
                    }
                }
            }
        }
    }

    private authCallback = (authErr: AuthError, response?: AuthResponse) => {
        if (authErr) {
            console.error(authErr);
            EventHandlerProvider.GetInstance().AddEvent(new VeriLiteErrorEvent(`${authErr.message} ${this.resources.IfThisProblemPersists} ${this.resources.VeriLiteSupportEmail}`, this.resources.AuthenticationError, []))
        }
    };

    public readErrorFromSession = (): string => {
        var error = sessionStorage.getItem(this.errorKey) as string;
        return error;
    };

    public refreshAccessToken = (): Promise<string> => {
        //console.log("refreshAccessToken");
        return new Promise<string>((resolve, reject) => {
            this.authClient.acquireTokenSilent(this.tokenRequest)
                .then((tokenReponse) => {
                    this.accessToken = tokenReponse.accessToken;
                    this.sessionExpiry = tokenReponse.expiresOn.getTime();
                    resolve(this.accessToken);
                }).catch((error) => {
                    console.error(error);
                    this.accessToken = "";
                    reject(error);
                });
        });
    };

    public getAccessToken = (): Promise<string> =>
        new Promise<string>((resolve, reject) => {
            if (String.isNullOrWhitespace(this.accessToken)) {
                this.refreshAccessToken().then((newToken) => {
                    resolve(newToken);
                }).catch((error) => {
                    reject(`Unable to retrieve access token: ${error}`);
                });
            } else {
                resolve(this.accessToken);
            }
        });

    public logout = () => {
        sessionStorage.removeItem(AuthenticationService.selectedLocationKey);
        this.authClient.logout();
    }

    public getUser = (): IAuthenticatedUser => {
        var userToken = this.authClient.getAccount().idToken;
        return {
            name: userToken.name,
            Location: userToken.extension_Location,
            Organisation: userToken.extension_Organization,
            TwoFactorEnabled: parseBoolean(userToken.extension_TwoFactorAuthentication),
            PasswordReset: parseBoolean(userToken.extension_PasswordReset),
            id: userToken.oid
        };
    };

    public passwordReset = () => {
        this.authClient.authority = process.env.REACT_APP_RESETPASSWORD || "";
        sessionStorage.setItem(this.passwordResetKey, 'true');
        this.authClient.loginRedirect(this.loginRequest);  
    }

    public editProfile = () => {
        this.authClient.authority = process.env.REACT_APP_EDITPROFILE || "";
        sessionStorage.setItem(this.editProfileKey, 'true');
        this.authClient.loginRedirect(this.loginRequest);
    }

    public reAuthenticateAfterPasswordReset = () => {
        sessionStorage.setItem(this.passwordResetKey, 'false');
        this.logout();
    }

    public setError = () => {
        this._state = AuthenticationState.Error;
    }    

    public setLicenseError = () => {
        this._state = AuthenticationState.LicenseError;
    }

    public twoFactorComplete = (code: string) => {
        this._state = AuthenticationState.Authenticated;
        sessionStorage.setItem(this.twoFactorKey, code);
    }

    public validateCodeAsync = async (code: string) => {
        var body = JSON.stringify({ code: code });
        return new Promise<IValidationResult>(res =>
            postJson(Endpoints.ValidateTwoFactorCode, body, undefined, true, new UserRequestHeaders(this.getUser()))
                .then((response: IValidationResult) => {
                    res(response);
                })
        );
    }

    public getTwoFactorCode = (): string|null => {
        return sessionStorage.getItem(this.twoFactorKey)
    }

    public getError = (): string => {
        return sessionStorage.getItem(this.errorKey) || "";
    }

    private initLogoutTimer = () => {
        this.secondsIdle = 0;
        setInterval(this.tickLogoutTimer, 1000);
        document.addEventListener("mousemove",
            this.resetLogoutTimer,
            true);
    };

    private resetLogoutTimer = () => {
        this.secondsIdle = 0;
    };

    private tickLogoutTimer = () => {
        this.secondsIdle++;
        if (this.secondsIdle === 20) {
            this.refreshAccessToken()
                .catch((e)=>
                    console.error(e)
                );
        }

        if (Date.now() >= this.sessionExpiry) {
            this.logout();
        }
    };

    private checkSessionState = (key:string) => {
        var sessionState = sessionStorage.getItem(key) as string;
        console.log(`Session state for ${key} is ${sessionState}`);
        return sessionState === 'true';
    }
}

export const Auth = new AuthenticationService();
